/**
 * SettingsPage — Subscription management, usage meters, pricing table.
 * Shows current plan, usage vs limits, and upgrade/downgrade/manage buttons.
 */

import { useState, useEffect } from 'react';
import { signOut } from 'aws-amplify/auth';
import { Crown, Zap, Rocket, ExternalLink, Loader2, Sun, Moon, Users, Check, Clock, X, AlertTriangle, Trash2, Compass, Brain, Share2, Copy, Bell, Smartphone } from 'lucide-react';
import { createCheckoutSession, createCustomerPortal, cancelSubscription, deleteAccount, getNotificationPreferences, updateNotificationPreferences, updateDailySettings, getAccountability, setAccountability, removeAccountability, setDriveLevel, setMbtiType, getRingShare, setRingShare, listRestSessions, updateRestPreferences } from '../lib/api';
import { TIERS } from '../lib/tiers';
import PricingTable from './PricingTable';
import { pushStatus, enablePush, disablePush, isIos, isStandalone } from '../lib/push';
import { sendTestPush } from '../lib/api';
import { canPromptInstall, onInstallAvailable, promptInstall } from '../lib/install';
import { trackAuthed } from '../lib/analytics';
import { useToast } from './Toast';
import VoiceTest from './VoiceTest';
import VoicePicker from './VoicePicker';

const TIER_ICONS = {
  free: Zap,
  pro: Crown,
  entrepreneur: Rocket,
};

function UsageMeter({ label, current, limit }) {
  const isUnlimited = limit === null || limit === undefined;
  const pct = isUnlimited ? 0 : Math.min((current / limit) * 100, 100);
  const atLimit = !isUnlimited && current >= limit;

  return (
    <div className="space-y-1.5">
      <div className="flex justify-between text-xs">
        <span className="text-slate-400">{label}</span>
        <span className={atLimit ? 'text-red-400 font-medium' : 'text-slate-300'}>
          {current} / {isUnlimited ? '\u221e' : limit}
        </span>
      </div>
      <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${atLimit ? 'bg-red-500' : 'bg-blue-500'}`}
          style={{ width: isUnlimited ? '0%' : `${pct}%` }}
        />
      </div>
    </div>
  );
}

const NOTIF_LABELS = {
  deadline: 'Deadline reminders (48hr & 2hr)',
  missed: 'Missed deadline alerts',
  summary: 'Weekly summary',
  reengagement: 'Re-engagement nudges',
  coaching: 'Coaching outreach (patterns, check-ins & decision reviews — max 1/week)',
  product: 'Product news (new features, a few times a year)',
};

const COMMON_TIMEZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Phoenix', 'America/Anchorage', 'Pacific/Honolulu',
  'Europe/London', 'Europe/Paris', 'Europe/Berlin',
  'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Kolkata',
  'Australia/Sydney', 'UTC'
];

const TIME_OPTIONS = Array.from({ length: 24 }, (_, h) => {
  const hh = String(h).padStart(2, '0');
  const label = h === 0 ? '12:00 AM' : h < 12 ? `${h}:00 AM` : h === 12 ? '12:00 PM' : `${h - 12}:00 PM`;
  return { value: `${hh}:00`, label };
});

function DailyPlanningSettings({ user }) {
  const [planTime, setPlanTime] = useState(user?.planning_reminder_time || '07:00');
  const [reviewTime, setReviewTime] = useState(user?.review_reminder_time || '');
  const [reviewEnabled, setReviewEnabled] = useState(!!user?.review_reminder_time);
  const [timezone, setTimezone] = useState(user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York');
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const handleSave = async (updates) => {
    setSaving(true);
    try {
      await updateDailySettings(updates);
      toast('Settings saved');
    } catch (err) {
      console.error('Failed to save daily settings:', err);
      toast('Failed to save settings', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-slate-800/60 border border-slate-700/50 rounded-lg p-5">
      <div className="flex items-center gap-2 mb-4">
        <Sun size={16} className="text-amber-400" />
        <h3 className="text-sm font-medium text-slate-200">Daily Planning</h3>
        {saving && <Loader2 size={12} className="animate-spin text-slate-400 ml-auto" />}
      </div>

      <div className="space-y-4">
        {/* Timezone */}
        <div>
          <label className="text-xs text-slate-400 block mb-1">Timezone</label>
          <select
            value={timezone}
            onChange={(e) => {
              setTimezone(e.target.value);
              handleSave({ timezone: e.target.value });
            }}
            className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {COMMON_TIMEZONES.map(tz => (
              <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>
            ))}
          </select>
        </div>

        {/* Planning reminder time */}
        <div>
          <label className="text-xs text-slate-400 block mb-1">Planning reminder</label>
          <select
            value={planTime}
            onChange={(e) => {
              setPlanTime(e.target.value);
              handleSave({ planning_reminder_time: e.target.value });
            }}
            className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {TIME_OPTIONS.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        {/* Review reminder toggle + time */}
        <div>
          <label className="flex items-center justify-between cursor-pointer mb-2">
            <div className="flex items-center gap-2">
              <Moon size={14} className="text-indigo-400" />
              <span className="text-xs text-slate-300">Evening review reminder</span>
            </div>
            <button
              onClick={() => {
                const newEnabled = !reviewEnabled;
                setReviewEnabled(newEnabled);
                handleSave({ review_reminder_time: newEnabled ? (reviewTime || '20:00') : null });
                if (newEnabled && !reviewTime) setReviewTime('20:00');
              }}
              className={`relative w-9 h-5 rounded-full transition-colors ${
                reviewEnabled ? 'bg-indigo-600' : 'bg-slate-600'
              }`}
            >
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                reviewEnabled ? 'translate-x-4' : 'translate-x-0'
              }`} />
            </button>
          </label>
          {reviewEnabled && (
            <select
              value={reviewTime || '20:00'}
              onChange={(e) => {
                setReviewTime(e.target.value);
                handleSave({ review_reminder_time: e.target.value });
              }}
              className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              {TIME_OPTIONS.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          )}
        </div>
      </div>
    </div>
  );
}

function AccountabilitySettings({ user }) {
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState(null);

  const isEntrepreneur = (user?.subscription_tier || user?.subscription_status) === 'entrepreneur'
    || user?.subscription_tier === 'entrepreneur';

  useEffect(() => {
    if (!isEntrepreneur) { setLoading(false); return; }
    getAccountability()
      .then(res => setPartner(res.partner))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isEntrepreneur]);

  if (!isEntrepreneur) return null;

  const handleInvite = async () => {
    if (!email.trim() || !name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const res = await setAccountability({ email: email.trim(), name: name.trim() });
      if (res.error) {
        setError(res.error);
      } else {
        setPartner(res.partner);
        setEmail('');
        setName('');
      }
    } catch (err) {
      setError('Failed to send invitation');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    setRemoving(true);
    try {
      await removeAccountability();
      setPartner(null);
    } catch (err) {
      setError('Failed to remove partner');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className="bg-slate-800/60 border border-slate-700/50 rounded-lg p-5">
      <div className="flex items-center gap-2 mb-4">
        <Users size={16} className="text-blue-400" />
        <h3 className="text-sm font-medium text-slate-200">Accountability Partner</h3>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Loader2 size={12} className="animate-spin" /> Loading...
        </div>
      ) : partner ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-200">{partner.name}</p>
              <p className="text-xs text-slate-500">{partner.email}</p>
            </div>
            <div className="flex items-center gap-2">
              {partner.status === 'active' ? (
                <span className="flex items-center gap-1 text-xs text-green-400 bg-green-900/30 px-2 py-1 rounded-full">
                  <Check size={10} /> Active
                </span>
              ) : (
                <span className="flex items-center gap-1 text-xs text-amber-400 bg-amber-900/30 px-2 py-1 rounded-full">
                  <Clock size={10} /> Pending
                </span>
              )}
              <button
                onClick={handleRemove}
                disabled={removing}
                className="text-xs text-slate-500 hover:text-red-400 transition-colors"
                title="Remove partner"
              >
                {removing ? <Loader2 size={12} className="animate-spin" /> : <X size={14} />}
              </button>
            </div>
          </div>
          <p className="text-xs text-slate-500">
            {partner.status === 'active'
              ? 'Your partner receives a weekly scorecard of commitments made vs kept. No session content is shared.'
              : 'Invitation sent. Your partner needs to click the link in their email to activate.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-slate-400">
            Designate someone to receive a weekly read-only scorecard of your commitments made vs kept. No session content is shared.
          </p>
          <div className="space-y-2">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Partner's name"
              className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Partner's email"
              className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={handleInvite}
            disabled={saving || !email.trim() || !name.trim()}
            className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:text-slate-500 text-white text-xs rounded-md transition-colors"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Users size={12} />}
            Send Invitation
          </button>
        </div>
      )}

      {error && (
        <p className="text-xs text-red-400 mt-2">{error}</p>
      )}
    </div>
  );
}

const DRIVE_LEVELS = [
  {
    value: 'coach',
    label: 'Coach',
    description: 'Same honesty, lighter push. Skips consequence-painting and accepts hedges the first time.',
  },
  {
    value: 'mentor',
    label: 'Mentor',
    description: 'Default. Warm but unsparing — names patterns, expects commitments, holds you to your word.',
  },
  {
    value: 'sparring_partner',
    label: 'Sparring Partner',
    description: 'Heavier push. Surfaces the cost of inaction early, rejects hedges firmly, leads with overdue assignments.',
  },
];

function DriveLevelSettings({ user }) {
  const [level, setLevel] = useState(user?.drive_level || 'mentor');
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const handleChange = async (newLevel) => {
    if (newLevel === level || saving) return;
    const previous = level;
    setLevel(newLevel);
    setSaving(true);
    try {
      const res = await setDriveLevel(newLevel);
      if (res?.error) {
        setLevel(previous);
        toast(res.error, { variant: 'error' });
      } else {
        toast('Drive level updated');
      }
    } catch {
      setLevel(previous);
      toast('Failed to update drive level', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-slate-800/60 border border-slate-700/50 rounded-lg p-5">
      <div className="flex items-center gap-2 mb-2">
        <Compass size={16} className="text-emerald-400" />
        <h3 className="text-sm font-medium text-slate-200">Navigator Drive Level</h3>
        {saving && <Loader2 size={12} className="animate-spin text-slate-400 ml-auto" />}
      </div>
      <p className="text-xs text-slate-400 mb-4">
        Controls how hard the Navigator pushes. Honesty stays the same at every level.
      </p>
      <div className="space-y-2">
        {DRIVE_LEVELS.map(opt => {
          const selected = level === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => handleChange(opt.value)}
              disabled={saving}
              className={`w-full text-left rounded-md border px-3 py-3 transition-colors ${
                selected
                  ? 'bg-emerald-900/20 border-emerald-700/60'
                  : 'bg-slate-900/40 border-slate-700/60 hover:border-slate-600'
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className={`text-sm font-medium ${selected ? 'text-emerald-300' : 'text-slate-200'}`}>
                  {opt.label}
                </span>
                {selected && <Check size={14} className="text-emerald-400" />}
              </div>
              <p className="text-xs text-slate-400">{opt.description}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

const MBTI_TYPES = [
  'INTJ', 'INTP', 'ENTJ', 'ENTP',
  'INFJ', 'INFP', 'ENFJ', 'ENFP',
  'ISTJ', 'ISFJ', 'ESTJ', 'ESFJ',
  'ISTP', 'ISFP', 'ESTP', 'ESFP',
];

function MbtiSettings({ user }) {
  const [type, setType] = useState(user?.mbti_type || '');
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const handleChange = async (next) => {
    if (saving) return;
    const previous = type;
    const newValue = next || null;
    setType(next);
    setSaving(true);
    try {
      const res = await setMbtiType(newValue);
      if (res?.error) {
        setType(previous);
        toast(res.error, { variant: 'error' });
      } else {
        toast(newValue ? `Communication style set to ${newValue}` : 'Communication style cleared');
      }
    } catch {
      setType(previous);
      toast('Failed to save', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-slate-800/60 border border-slate-700/50 rounded-lg p-5">
      <div className="flex items-center gap-2 mb-2">
        <Brain size={16} className="text-purple-400" />
        <h3 className="text-sm font-medium text-slate-200">Communication Style (MBTI)</h3>
        {saving && <Loader2 size={12} className="animate-spin text-slate-400 ml-auto" />}
      </div>
      <p className="text-xs text-slate-400 mb-4">
        Optional. If you know your type, the Navigator will adapt how it frames things — not what it says.
      </p>
      <select
        value={type}
        onChange={(e) => handleChange(e.target.value)}
        className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:outline-none focus:ring-1 focus:ring-purple-500"
      >
        <option value="">Prefer not to say</option>
        {MBTI_TYPES.map(t => (
          <option key={t} value={t}>{t}</option>
        ))}
      </select>
      <p className="text-xs text-slate-500 mt-2">
        Don't know your type?{' '}
        <a
          href="https://www.16personalities.com/free-personality-test"
          target="_blank"
          rel="noopener noreferrer"
          className="text-purple-400 hover:text-purple-300 underline"
        >
          Take a free 10-minute test
        </a>
      </p>
    </div>
  );
}

function RingShareSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [url, setUrl] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    getRingShare()
      .then((res) => { setEnabled(!!res.enabled); setUrl(res.url || null); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const toggle = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const res = await setRingShare(!enabled);
      setEnabled(!!res.enabled);
      setUrl(res.url || null);
    } catch {
      // leave state as-is
    } finally {
      setSaving(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };

  const shareText = encodeURIComponent('My follow-through, counted honestly. The misses count too. What’s your number?');

  return (
    <div className="bg-slate-800/60 border border-slate-700/50 rounded-lg p-5">
      <div className="flex items-center gap-2 mb-2">
        <Share2 size={16} className="text-blue-400" />
        <h3 className="text-sm font-medium text-slate-200">Public follow-through page</h3>
      </div>
      <p className="text-xs text-slate-500 leading-relaxed mb-4">
        Off by default. When on, anyone with your link sees your Ring numbers only:
        kept, missed, and in flight over the last 30 days, with the definition attached.
        Never the commitments themselves, never your name. Turning it off kills the
        link instantly; turning it back on mints a fresh one.
      </p>
      {loading ? (
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Loader2 size={12} className="animate-spin" /> Loading...
        </div>
      ) : (
        <div className="space-y-3">
          <button
            onClick={toggle}
            disabled={saving}
            className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${enabled
              ? 'border-green-700/60 bg-green-900/30 text-green-300 hover:bg-green-900/50'
              : 'border-slate-600 bg-slate-700/40 text-slate-300 hover:bg-slate-700'}`}
          >
            {saving ? <Loader2 size={12} className="animate-spin inline" /> : (enabled ? 'Sharing is ON — turn off' : 'Turn on public page')}
          </button>
          {enabled && url && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <code className="text-[11px] text-slate-400 bg-slate-900/60 rounded px-2 py-1.5 truncate flex-1">{url}</code>
                <button onClick={copy} className="text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1">
                  {copied ? <Check size={12} className="text-green-400" /> : <Copy size={12} />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              <div className="flex items-center gap-3 text-xs">
                <a className="text-blue-400 hover:text-blue-300" target="_blank" rel="noreferrer"
                   href={`https://twitter.com/intent/tweet?text=${shareText}&url=${encodeURIComponent(url)}`}>Share on X</a>
                <a className="text-blue-400 hover:text-blue-300" target="_blank" rel="noreferrer"
                   href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`}>LinkedIn</a>
                <a className="text-blue-400 hover:text-blue-300" target="_blank" rel="noreferrer"
                   href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`}>Facebook</a>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const AUTO_OPTIONS = [['prime', 'Morning'], ['rest', 'Evening'], ['both', 'Both'], ['off', 'Off']];

function DeepRestSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tonesOn, setTonesOn] = useState(true);
  const [enabled, setEnabled] = useState(true);
  const [prefs, setPrefs] = useState({ auto: 'rest', space_scale: 1, music_db: 0 });

  useEffect(() => {
    listRestSessions({ limit: 1 })
      .then((res) => {
        setTonesOn(res.tones_on !== false);
        setEnabled(res.enabled !== false);
        if (res.prefs) setPrefs(res.prefs);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const apply = async (patch) => {
    if (saving) return;
    setSaving(true);
    try {
      const res = await updateRestPreferences(patch);
      if (res.prefs) { setPrefs(res.prefs); setTonesOn(res.prefs.tones_on !== false); }
      else if (typeof res.tones_on === 'boolean') setTonesOn(res.tones_on);
    } catch { /* leave as is */ } finally {
      setSaving(false);
    }
  };
  const toggle = () => apply({ tones_on: !tonesOn });

  if (!enabled) return null;
  const tuned = Math.abs((prefs.space_scale || 1) - 1) > 0.01 || Math.abs(prefs.music_db || 0) > 0.01;

  return (
    <div className="bg-slate-800/60 border border-slate-700/50 rounded-lg p-5">
      <div className="flex items-center gap-2 mb-2">
        <Moon size={16} className="text-indigo-400" />
        <h3 className="text-sm font-medium text-slate-200">Deep Rest</h3>
      </div>
      <p className="text-xs text-slate-500 leading-relaxed mb-4">
        A short guided rest and rehearsal, eight to ten minutes, built from your own
        commitments and trip wires, in your chosen voice. One is made after each evening review.
        Ambient tones are a soft two-ear layer under the voice; headphones make
        them work, and they are off with one tap if you'd rather have voice and
        music only. The change applies to sessions made from now on.
      </p>
      {loading ? (
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Loader2 size={12} className="animate-spin" /> Loading...
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <p className="text-xs text-slate-400 mb-1.5">Automatic sessions</p>
            <div className="inline-flex rounded-md border border-slate-700 overflow-hidden" role="group" aria-label="Automatic sessions">
              {AUTO_OPTIONS.map(([k, label]) => (
                <button key={k} onClick={() => apply({ auto: k })} disabled={saving} aria-pressed={prefs.auto === k}
                  className={`px-3 py-1.5 text-xs ${prefs.auto === k ? 'bg-indigo-900/50 text-indigo-200' : 'text-slate-400 hover:text-slate-200'}`}>
                  {label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-slate-500 mt-1.5">
              Morning makes a Prime right after you plan the day. Evening makes a Rest after the review. Both do both. Sessions you start yourself count toward your monthly limit; automatic ones do not.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={toggle}
              disabled={saving}
              className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${tonesOn
                ? 'border-indigo-700/60 bg-indigo-900/30 text-indigo-200 hover:bg-indigo-900/50'
                : 'border-slate-600 bg-slate-700/40 text-slate-300 hover:bg-slate-700'}`}
            >
              {saving ? <Loader2 size={12} className="animate-spin inline" /> : (tonesOn ? 'Ambient tones: ON' : 'Ambient tones: OFF')}
            </button>
            {tuned && (
              <button onClick={() => apply({ reset_tuning: true })} disabled={saving} className="text-xs text-slate-400 hover:text-slate-200">
                Reset tuning (pace ×{Number(prefs.space_scale).toFixed(2)}, music {prefs.music_db > 0 ? '+' : ''}{Number(prefs.music_db).toFixed(0)} dB)
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function PushSettings() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [installable, setInstallable] = useState(canPromptInstall());

  useEffect(() => {
    pushStatus().then(setStatus).catch(() => setStatus({ supported: false }));
    return onInstallAvailable(setInstallable);
  }, []);

  const enable = async () => {
    setBusy(true); setMsg(null);
    try {
      await enablePush();
      trackAuthed('push_enabled', {});
      setMsg('On. You will get a note when a Prime or Rest is ready, and at your planning time.');
    } catch (e) {
      setMsg(e.message || 'Could not turn notifications on.');
    } finally {
      setBusy(false);
      pushStatus().then(setStatus).catch(() => {});
    }
  };
  const disable = async () => {
    setBusy(true); setMsg(null);
    try { await disablePush(); setMsg('Off.'); } finally { setBusy(false); pushStatus().then(setStatus).catch(() => {}); }
  };
  const test = async () => {
    setBusy(true); setMsg(null);
    try {
      const res = await sendTestPush();
      if (res.error) setMsg(res.error);
      else setMsg(res.sent ? 'Sent. It should appear in a moment, even if you leave the app.' : 'Nothing was sent; the device may need to re-enable notifications.');
    } catch { setMsg('Could not send the test.'); } finally { setBusy(false); }
  };
  const install = async () => {
    trackAuthed('install_prompt', {});
    const outcome = await promptInstall();
    setInstallable(canPromptInstall());
    if (outcome === 'accepted') setMsg('Installed. Open it from your home screen.');
  };

  const on = !!status && status.subscribed && status.serverCount > 0;
  const iosNeedsInstall = isIos() && !isStandalone();

  return (
    <div className="bg-slate-800/60 border border-slate-700/50 rounded-lg p-5">
      <div className="flex items-center gap-2 mb-2">
        <Bell size={16} className="text-amber-400" />
        <h3 className="text-sm font-medium text-slate-200">Notifications on this device</h3>
      </div>
      <p className="text-xs text-slate-500 leading-relaxed mb-4">
        A short note when your morning Prime or evening Rest is ready, and one at your planning time. Nothing else. Per device; turn it on where you listen.
      </p>
      {!status ? (
        <div className="flex items-center gap-2 text-xs text-slate-500"><Loader2 size={12} className="animate-spin" /> Checking...</div>
      ) : !status.supported ? (
        <p className="text-xs text-slate-500">This browser does not support notifications.</p>
      ) : status.configured === false ? (
        <p className="text-xs text-slate-500">Not set up on the server yet.</p>
      ) : (
        <div className="space-y-3">
          {iosNeedsInstall && (
            <p className="text-xs text-amber-300/90 flex items-start gap-2">
              <Smartphone size={14} className="mt-0.5 flex-shrink-0" />
              On iPhone, notifications need the app on your Home Screen: tap Share, then Add to Home Screen, and open it from there.
            </p>
          )}
          <div className="flex flex-wrap items-center gap-3">
            {on ? (
              <>
                <button onClick={test} disabled={busy} className="text-xs px-3 py-1.5 rounded-md bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 text-white">
                  {busy ? <Loader2 size={12} className="animate-spin inline" /> : 'Send a test notification'}
                </button>
                <button onClick={disable} disabled={busy} className="text-xs px-3 py-1.5 rounded-md border border-green-700/60 bg-green-900/30 text-green-300 hover:bg-green-900/50">
                  Notifications: ON, turn off
                </button>
              </>
            ) : (
              <button onClick={enable} disabled={busy || status.permission === 'denied'} className="text-xs px-3 py-1.5 rounded-md border border-slate-600 bg-slate-700/40 text-slate-300 hover:bg-slate-700 disabled:opacity-60">
                {busy ? <Loader2 size={12} className="animate-spin inline" /> : 'Turn notifications on'}
              </button>
            )}
            {installable && (
              <button onClick={install} className="text-xs px-3 py-1.5 rounded-md border border-indigo-700/60 bg-indigo-900/30 text-indigo-200 hover:bg-indigo-900/50 flex items-center gap-1.5">
                <Smartphone size={12} /> Add to home screen
              </button>
            )}
          </div>
          {status.permission === 'denied' && <p className="text-[11px] text-slate-500">Notifications are blocked for this site in the browser settings.</p>}
          {msg && <p className="text-xs text-slate-400">{msg}</p>}
        </div>
      )}
    </div>
  );
}

function DangerZone({ user, isPaying }) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [typedEmail, setTypedEmail] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const { toast } = useToast();

  const userEmail = (user?.email || '').trim().toLowerCase();
  const canDelete = typedEmail.trim().toLowerCase() === userEmail && userEmail.length > 0;

  const handleDelete = async () => {
    if (!canDelete || deleting) return;
    setDeleting(true);
    setError(null);
    try {
      const res = await deleteAccount(typedEmail.trim().toLowerCase());
      if (res?.error) {
        setError(res.error);
        setDeleting(false);
        return;
      }
      toast('Account deleted. Signing you out...');
      try { await signOut(); } catch { /* ignore */ }
      window.location.reload();
    } catch (err) {
      setError('Failed to delete account. Please try again.');
      setDeleting(false);
    }
  };

  return (
    <div className="bg-red-950/20 border border-red-900/40 rounded-lg p-5">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle size={16} className="text-red-400" />
        <h3 className="text-sm font-medium text-red-300">Danger Zone</h3>
      </div>
      <p className="text-xs text-slate-400 mb-4">
        Permanently delete your account and all session, onboarding, and progress data.
        This cannot be undone.{isPaying ? ' Your subscription will be cancelled immediately.' : ''}
      </p>

      {!confirmOpen ? (
        <button
          onClick={() => setConfirmOpen(true)}
          className="flex items-center gap-1.5 px-4 py-2 bg-red-900/40 hover:bg-red-900/60 border border-red-800/50 text-red-300 text-xs rounded-md transition-colors"
        >
          <Trash2 size={12} />
          Delete account and data
        </button>
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-slate-300">
            Type <span className="font-mono text-red-300">{userEmail}</span> to confirm:
          </p>
          <input
            type="email"
            value={typedEmail}
            onChange={(e) => setTypedEmail(e.target.value)}
            placeholder="your email"
            autoComplete="off"
            className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-red-500"
          />
          <div className="flex items-center gap-2">
            <button
              onClick={handleDelete}
              disabled={!canDelete || deleting}
              className="flex items-center gap-1.5 px-4 py-2 bg-red-600 hover:bg-red-500 disabled:bg-slate-700 disabled:text-slate-500 text-white text-xs rounded-md transition-colors"
            >
              {deleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
              Permanently delete
            </button>
            <button
              onClick={() => { setConfirmOpen(false); setTypedEmail(''); setError(null); }}
              disabled={deleting}
              className="px-4 py-2 text-xs text-slate-400 hover:text-slate-200 transition-colors"
            >
              Cancel
            </button>
          </div>
          {error && <p className="text-xs text-red-400">{error}</p>}
        </div>
      )}
    </div>
  );
}

export default function SettingsPage({ subscription, user }) {
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [cancelMessage, setCancelMessage] = useState(null);
  const [planChangeMessage, setPlanChangeMessage] = useState(null);
  const [notifPrefs, setNotifPrefs] = useState(null);
  const [notifSaving, setNotifSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    getNotificationPreferences()
      .then(res => setNotifPrefs(res.preferences))
      .catch(() => setNotifPrefs({ deadline: true, missed: true, summary: true, reengagement: true, coaching: true }));
  }, []);

  const toggleNotifPref = async (key) => {
    const updated = { ...notifPrefs, [key]: !notifPrefs[key] };
    setNotifPrefs(updated);
    setNotifSaving(true);
    try {
      await updateNotificationPreferences({ [key]: updated[key] });
    } catch {
      setNotifPrefs(notifPrefs);
    } finally {
      setNotifSaving(false);
    }
  };

  const currentTier = subscription?.tier || 'free';
  const usage = subscription?.usage || {};
  const limits = subscription?.limits || TIERS.free;

  const handleSelectTier = async (tier, interval = 'monthly') => {
    setCheckoutLoading(`${tier}_${interval}`);
    setPlanChangeMessage(null);
    try {
      const res = await createCheckoutSession(tier, interval);
      if (res.checkout_url) {
        window.open(res.checkout_url, '_blank');
      } else if (res.plan_changed) {
        // Direct plan change for existing subscribers (no checkout needed)
        setPlanChangeMessage(`Your plan has been changed to ${res.new_tier_name}. Refresh to see updated limits.`);
      }
    } catch (err) {
      console.error('Checkout error:', err);
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleManage = async () => {
    setPortalLoading(true);
    try {
      const res = await createCustomerPortal();
      if (res.portal_url) {
        window.open(res.portal_url, '_blank');
      }
    } catch (err) {
      console.error('Portal error:', err);
    } finally {
      setPortalLoading(false);
    }
  };

  const handleCancel = async () => {
    setCancelLoading(true);
    try {
      const res = await cancelSubscription();
      if (res?.error) {
        toast(res.error, { variant: 'error' });
      } else {
        const until = res?.access_until ? new Date(res.access_until * 1000).toLocaleDateString() : null;
        setCancelMessage(until
          ? `Subscription cancelled. You'll keep access until ${until}.`
          : 'Subscription cancelled.');
        setCancelConfirmOpen(false);
      }
    } catch (err) {
      console.error('Cancel error:', err);
      toast('Failed to cancel subscription', { variant: 'error' });
    } finally {
      setCancelLoading(false);
    }
  };

  const status = user?.subscription_status || 'trial';
  const isTrialing = status === 'trialing';
  const isPaying = status === 'active' && currentTier !== 'free';
  const trialEnds = user?.subscription_expires_at ? new Date(user.subscription_expires_at) : null;
  const trialDaysLeft = trialEnds ? Math.max(0, Math.ceil((trialEnds - new Date()) / 86400000)) : null;

  return (
    <div className="h-full overflow-auto">
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-8">

        {/* Current Plan Card */}
        <div className="bg-slate-800/60 border border-slate-700/50 rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              {(() => {
                const Icon = TIER_ICONS[currentTier] || Zap;
                return <Icon size={20} className={currentTier === 'entrepreneur' ? 'text-amber-400' : currentTier === 'pro' ? 'text-blue-400' : 'text-slate-400'} />;
              })()}
              <div>
                <h2 className="text-sm font-medium text-slate-200">
                  {TIERS[currentTier]?.name || 'Free'} Plan
                </h2>
                <p className="text-xs text-slate-500">
                  {isTrialing
                    ? `Free trial \u2014 ${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'} left${trialEnds ? ` \u00b7 ends ${trialEnds.toLocaleDateString()}` : ''}`
                    : isPaying
                      ? `Active${user?.subscription_expires_at ? ` \u2014 renews ${new Date(user.subscription_expires_at).toLocaleDateString()}` : ''}`
                      : 'No active subscription'}
                </p>
              </div>
            </div>
            {(isPaying || isTrialing) && (
              <div className="flex items-center gap-3">
                <button
                  onClick={handleManage}
                  disabled={portalLoading}
                  className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200 transition-colors"
                >
                  {portalLoading ? <Loader2 size={12} className="animate-spin" /> : <ExternalLink size={12} />}
                  Manage Subscription
                </button>
                {!cancelConfirmOpen ? (
                  <button
                    onClick={() => setCancelConfirmOpen(true)}
                    className="text-xs text-slate-500 hover:text-red-400 transition-colors"
                  >
                    Cancel
                  </button>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={handleCancel}
                      disabled={cancelLoading}
                      className="flex items-center gap-1 px-2 py-1 bg-red-900/40 hover:bg-red-900/60 border border-red-800/50 text-red-300 text-xs rounded transition-colors"
                    >
                      {cancelLoading ? <Loader2 size={10} className="animate-spin" /> : null}
                      Confirm cancel
                    </button>
                    <button
                      onClick={() => setCancelConfirmOpen(false)}
                      disabled={cancelLoading}
                      className="text-xs text-slate-500 hover:text-slate-300 transition-colors"
                    >
                      Keep
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {cancelMessage && (
            <div className="mb-3 bg-amber-900/20 border border-amber-700/40 rounded p-2">
              <p className="text-xs text-amber-300">{cancelMessage}</p>
            </div>
          )}

          {/* Usage meters */}
          <div className="space-y-3">
            <UsageMeter
              label="Sessions this month"
              current={usage.sessions_this_month || 0}
              limit={limits.sessions_per_month}
            />
            <UsageMeter
              label="Threads"
              current={usage.threads || 0}
              limit={limits.threads}
            />
            <UsageMeter
              label="Active assignments"
              current={usage.active_assignments || 0}
              limit={limits.active_assignments}
            />
          </div>
        </div>

        {/* Plan change success message */}
        {planChangeMessage && (
          <div className="bg-green-900/30 border border-green-700/50 rounded-lg p-3">
            <p className="text-xs text-green-300">{planChangeMessage}</p>
          </div>
        )}

        {/* Daily Planning Settings */}
        <DailyPlanningSettings user={user} />

        {/* Navigator Drive Level */}
        <DriveLevelSettings user={user} />

        {/* MBTI Communication Style */}
        <MbtiSettings user={user} />

        {/* Accountability Partner */}
        <AccountabilitySettings user={user} />

        {/* Public Ring share */}
        <RingShareSettings />

        <DeepRestSettings />

        <PushSettings />

        {/* Voice picker (Pro + Entrepreneur) */}
        {(currentTier === 'pro' || currentTier === 'entrepreneur') && (
          <VoicePicker currentVoiceId={user?.voice_id} />
        )}

        {/* Voice Test (Pro + Entrepreneur — beta) */}
        {(currentTier === 'pro' || currentTier === 'entrepreneur') && <VoiceTest />}

        {/* Notification Preferences */}
        {notifPrefs && (
          <div className="bg-slate-800/60 border border-slate-700/50 rounded-lg p-5">
            <h3 className="text-sm font-medium text-slate-200 mb-4">Email Notifications</h3>
            <div className="space-y-3">
              {Object.entries(NOTIF_LABELS).map(([key, label]) => (
                <label key={key} className="flex items-center justify-between cursor-pointer">
                  <span className="text-xs text-slate-300">{label}</span>
                  <button
                    onClick={() => toggleNotifPref(key)}
                    disabled={notifSaving}
                    className={`relative w-9 h-5 rounded-full transition-colors ${
                      notifPrefs[key] ? 'bg-blue-600' : 'bg-slate-600'
                    }`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                      notifPrefs[key] ? 'translate-x-4' : 'translate-x-0'
                    }`} />
                  </button>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Pricing Table */}
        <div>
          <h3 className="text-sm font-medium text-slate-300 mb-4">Plans</h3>
          <PricingTable
            currentTier={currentTier}
            onSelectTier={handleSelectTier}
            isAuthenticated={true}
            checkoutLoading={checkoutLoading}
            trialEligible={!user?.subscription_started_at}
          />
        </div>

        {/* Danger Zone — irreversible account/data deletion */}
        <DangerZone user={user} isPaying={isPaying || isTrialing} />

      </div>
    </div>
  );
}
